import { useState } from 'react';

type ModalListState<T extends readonly string[]> = {
  [Key in `${T[number]}Modal`]: ModalState;
};

export type ModalState = {
  close: () => void;
  open: () => void;
  toggle: () => void;
  visible: boolean;
};

export const useToggleModal = <T extends readonly string[]>(modals: [...T]) => {
  const initValues = modals.reduce(
    (acc, cur) => {
      acc[`${cur}Modal`] = {
        visible: false,
        close: () => closeModal(`${cur}Modal`),
        open: () => openModal(`${cur}Modal`),
        toggle: () => toggleModal(`${cur}Modal`),
      };
      return acc;
    },
    {} as Record<string, ModalState>,
  );
  const [modalsType, setModalsType] =
    useState<Record<string, ModalState>>(initValues);

  const closeAllModal = () =>
    setModalsType(prev => {
      if (Object.values(prev).every(value => !value.visible)) return prev;

      const newState = Object.entries(prev).reduce(
        (acc, [key, value]) => {
          acc[key] = { ...value, visible: false };
          return acc;
        },
        {} as Record<string, ModalState>,
      );
      return { ...prev, ...newState };
    });

  // Bails out (returns the same reference) when the value wouldn't actually
  // change. Without this, calling close()/open() on an already-closed/open
  // modal still produces a new object reference every time, which loops
  // forever if a caller's effect depends on this modal's state and calls
  // close()/open() unconditionally on every run (see FirstLoginPromptModal).
  const closeModal = (modalName: string) => {
    return setModalsType(prev => {
      if (!prev[modalName].visible) return prev;
      return {
        ...prev,
        [modalName]: { ...prev[modalName], visible: false },
      };
    });
  };

  const openModal = (modalName: string) => {
    return setModalsType(prev => {
      if (prev[modalName].visible) return prev;
      return {
        ...prev,
        [modalName]: { ...prev[modalName], visible: true },
      };
    });
  };

  const toggleModal = (modalName: string) => {
    return setModalsType(prev => {
      return {
        ...prev,
        [modalName]: { ...prev[modalName], visible: !prev[modalName].visible },
      };
    });
  };

  return { ...(modalsType as ModalListState<T>), closeAllModal };
};
